import React, { useCallback, useMemo, useState } from "react";
import { Editor } from "@tiptap/core";
import styled from "styled-components";
import Button from "../../Button";
import Input from "../../Input";
import PopupMenu from "../../PopupMenu";

const Wrapper = styled.div`
  padding: 16px;
  gap: 8px;
  display: flex;
  flex-direction: column;
`;
type Props = {
  editor: Editor;
  setEditingLabel: React.Dispatch<React.SetStateAction<boolean>>;
  source: string;
  labelRef: React.RefObject<HTMLDivElement>;
  isFocused: boolean;
};

const LinkPopupMenu = ({ editor, setEditingLabel, source, labelRef, isFocused }: Props) => {
  const selectedText = editor.state.selection.content().size
    ? editor.state.doc.textBetween(editor.state.selection.from, editor.state.selection.to)
    : "";
  const hasLink = useMemo(() => Boolean(editor?.getAttributes("link")?.href), [editor]);
  const [url, setUrl] = useState<string>((editor?.getAttributes("link")?.href as string) || "");
  const [label, setLabel] = useState<string>(selectedText);

  const setLink = useCallback(() => {
    if (!url || url.trim() === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      setEditingLabel(false);
      return;
    }
    const formattedUrl = url.startsWith("http") ? url : `https://${url}`;
    const { from, to } = editor.state.selection;
    const hasLabel = label.trim().length > 0;
    editor.chain().focus();
    if (hasLabel) {
      editor
        .chain()
        .deleteRange({ from, to })
        .insertContentAt(from, {
          type: "text",
          text: label.trim(),
          marks: [{ type: "link", attrs: { href: formattedUrl } }],
        })
        .run();
    } else {
      editor.chain().extendMarkRange("link").setLink({ href: formattedUrl }).run();
    }
    setEditingLabel(false);
  }, [editor, url, label, setEditingLabel]);

  const removeLink = useCallback(() => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    setEditingLabel(false);
  }, [editor, setEditingLabel]);

  if (!isFocused) {
    return null;
  }

  return (
    <>
      <PopupMenu
        source={source}
        vPos="floatBelow"
        hPos="alignLeft"
        stayOpenOnClick
        width="300px"
        close={() => setEditingLabel(false)}>
        <Wrapper ref={labelRef}>
          <Input
            label="Label"
            placeholder="example text"
            value={label}
            onChange={e => setLabel(e.target.value)}
          />

          <Input label="URL" value={url} onChange={e => setUrl(e.target.value)} />

          <Button onClick={setLink} text={hasLink ? "Update link" : "Insert link"} flex="1" />
          {hasLink && (
            <Button
              mt="xs"
              size="small"
              buttonStyle="tertiary"
              onClick={removeLink}
              text="Remove link"
            />
          )}
        </Wrapper>
      </PopupMenu>
    </>
  );
};

export default React.memo(LinkPopupMenu);
